import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null,
  requiredPermission = null,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated: authStateAuthenticated, loading: authStateLoading, hasPermission, role: authStateRole } = useAuth();
  const location = useLocation();
  
  // Get token and stored user synchronously for immediate check on refresh
  const token = localStorage.getItem('token');
  const storedUserStr = localStorage.getItem('user');
  let storedUser = null;
  try {
    storedUser = storedUserStr ? JSON.parse(storedUserStr) : null;
  } catch (e) {}

  const currentRole = authStateRole || storedUser?.role?.toLowerCase();
  const isAuthenticated = authStateAuthenticated || (!!token && !!storedUser);

  // 1. If we are definitively NOT authenticated (no token and no loading), go to login
  if (!token && !authStateLoading) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // 2. If we are loading, wait
  // CRITICAL: We stay in this loading state instead of redirecting to /unauthorized
  if (authStateLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <LoadingSpinner size="lg" text="Verifying session..." />
      </div>
    );
  }

  
  // 3. Role Check - only perform after loading is finished and we have a role
  if (requiredRole && currentRole) {
    if (currentRole !== requiredRole) {
      // If role definitively doesn't match after loading/sync
      return (
        <Navigate 
          to="/unauthorized" 
          state={{ message: `Access denied. Required: ${requiredRole}, Current: ${currentRole}` }} 
          replace 
        />
      );
    }
  }

  // 4. Permission check
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ message: "Access denied. Insufficient permissions." }} 
        replace 
      />
    );
  }

  // 5. Final fallback for unauthenticated users who finished loading
  if (!authStateLoading && !isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
